import { useContext, useEffect, useState } from "react";

import { DialogContext } from "@/components/layout/DialogProvider";
import { useAuth } from "@/components/lib/auth";
import { ScenarioContextMultiSelect } from "@/components/pages_logged_in/roleplays/details/create/ScenarioContextMultiSelect";
import { WorkspaceContextForm } from "@/components/pages_logged_in/roleplays/details/create/WorkspaceContextForm";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  useCreateWorkspaceContextMutation,
  useDeleteWorkspaceContextMutation,
  useUpdateWorkspaceContextMutation,
  useWorkspaceContextsQuery,
  WorkspaceContextFragment,
} from "@/graphql";
import { UploadedFile } from "@/lib/upload-file";

interface ScenarioContextsProps {
  onChange: (contexts: WorkspaceContextFragment[]) => void;
}

export function ScenarioContexts({ onChange }: ScenarioContextsProps) {
  const [isOpenContextDrawer, setIsOpenContextDrawer] = useState(false);
  const [selectedContexts, setSelectedContexts] = useState<
    WorkspaceContextFragment[]
  >([]);
  const [currentEditContext, setCurrentEditContext] =
    useState<WorkspaceContextFragment | null>(null);
  const { currentWorkspace } = useAuth();

  // Get workspace contexts
  const { data: workspaceContexts, refetch } = useWorkspaceContextsQuery({
    variables: { workspaceId: currentWorkspace?.id || "", pageSize: 5 },
    fetchPolicy: "network-only",
    skip: !currentWorkspace?.id,
  });
  const workspaceContextsData = workspaceContexts?.workspaceContexts.data || [];
  const { showConfirmationDialog } = useContext(DialogContext);

  // Create, update, delete hooks
  const [createWorkspaceContext, { loading: isCreatingContext }] =
    useCreateWorkspaceContextMutation({
      onCompleted: () => {
        setIsOpenContextDrawer(false);
        refetch();
      },
    });

  const [updateWorkspaceContext, { loading: isUpdatingContext }] =
    useUpdateWorkspaceContextMutation({
      onCompleted: () => {
        setIsOpenContextDrawer(false);
        setCurrentEditContext(null);
        refetch();
      },
      onError: (error) => {
        console.error("Failed to update context:", error);
      },
    });

  const [deleteWorkspaceContext] = useDeleteWorkspaceContextMutation({
    onCompleted: () => {
      refetch();
    },
  });

  /**
   * Handles selection of workspace contexts by their IDs.
   * @param selectedIds Array of selected context IDs
   */
  const handleSetSelectedContexts = (selectedIds: string[]) => {
    const filteredContexts = workspaceContextsData.filter((context) =>
      selectedIds.includes(context.id),
    );
    setSelectedContexts(filteredContexts);
  };

  /**
   * Handles form submission for create/update context.
   */
  const handleSubmitForm = async (data: {
    title: string;
    context: string;
    attachments?: Array<UploadedFile>;
  }) => {
    try {
      if (currentEditContext) {
        await updateWorkspaceContext({
          variables: {
            id: currentEditContext.id,
            input: {
              title: data.title,
              context: data.context,
              fileIds: data.attachments?.map((file) => parseInt(file.id)) || [],
            },
          },
        });
      } else {
        await createWorkspaceContext({
          variables: {
            input: {
              title: data.title,
              context: data.context,
              fileIds: data.attachments?.map((file) => parseInt(file.id)) || [],
            },
          },
        });
      }
    } catch (error) {
      console.error("Failed to submit context form:", error);
    }
  };

  const handleEditContext = (contextId: string) => {
    const context = workspaceContextsData.find((c) => c.id === contextId);
    setCurrentEditContext(context || null);
    setIsOpenContextDrawer(true);
  };

  const handleDeleteContext = async (contextId: string) => {
    const context = workspaceContextsData.find((c) => c.id === contextId);

    if (!context) {
      return;
    }

    showConfirmationDialog({
      title: "Delete Context",
      description: `Are you sure you want to delete this context - ${context.title}?`,
      intent: "danger",
      onConfirm: () => handleConfirmDelete(contextId),
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
      onCancel: () => {},
    });
  };

  const handleConfirmDelete = async (contextId: string) => {
    await deleteWorkspaceContext({ variables: { id: contextId } });
    setSelectedContexts((prevSelectedContexts) =>
      prevSelectedContexts.filter((context) => context.id !== contextId),
    );
  };

  // Close drawer and reset edit state
  const handleCloseDrawer = () => {
    setIsOpenContextDrawer(false);
    setCurrentEditContext(null);
  };

  useEffect(() => {
    if (onChange) {
      onChange(selectedContexts);
    }
  }, [selectedContexts]);

  const options = workspaceContextsData.map((context) => ({
    value: context.id,
    label: context.title,
  }));
  const selected = selectedContexts.map((context) => context.id);

  if (currentWorkspace.isPersonal) {
    return null;
  }

  return (
    <div className="flex items-center gap-6 rounded-md">
      <ScenarioContextMultiSelect
        options={options}
        selected={selected}
        onChange={handleSetSelectedContexts}
        selectedContexts={selectedContexts}
        onDelete={handleDeleteContext}
        onEdit={handleEditContext}
        onCreate={() => {
          setIsOpenContextDrawer(true);
          setCurrentEditContext(null);
        }}
        totalRecords={workspaceContexts?.workspaceContexts.totalRecords || 0}
        onSearch={async (query) => {
          refetch({
            workspaceId: currentWorkspace?.id || "",
            query,
          });
        }}
      />

      <AddSavedContextDrawer
        open={isOpenContextDrawer}
        onOpenChange={handleCloseDrawer}
        onSubmit={handleSubmitForm}
        existingContext={
          currentEditContext
            ? {
                ...currentEditContext,
                attachments: currentEditContext?.attachments.map((att) => ({
                  file: {
                    ...att.file,
                    type: att.file.type ?? "unknown",
                  },
                })),
              }
            : null
        }
        isLoading={isCreatingContext || isUpdatingContext}
      />
    </div>
  );
}

interface AddSavedContextDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: {
    title: string;
    context: string;
    attachments?: Array<UploadedFile>;
  }) => void;
  existingContext?: {
    id: string;
    title: string;
    context: string;
    attachments?: Array<{
      file: UploadedFile;
    }>;
  } | null;
  isLoading?: boolean;
}

function AddSavedContextDrawer({
  open,
  onOpenChange,
  onSubmit,
  existingContext,
  isLoading = false,
}: AddSavedContextDrawerProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="z-[9999] md:min-w-[600px] w-full p-3">
        <SheetHeader>
          <SheetTitle>
            {existingContext ? "Edit Saved Context" : "Create Saved Context"}
          </SheetTitle>
          <SheetDescription>
            Attach this to scenarios for faster, more consistent results
          </SheetDescription>
        </SheetHeader>
        <WorkspaceContextForm
          onSubmit={onSubmit}
          defaultValues={
            existingContext
              ? {
                  title: existingContext.title,
                  context: existingContext.context,
                  attachments: existingContext.attachments
                    ? existingContext.attachments.map(
                        (attachment) => attachment.file,
                      )
                    : [],
                }
              : undefined
          }
        />

        <SheetFooter className="flex justify-between">
          <SheetClose asChild>
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
          </SheetClose>

          <Button
            type="submit"
            form="workspace-context-form"
            className="flex-1"
            disabled={isLoading}
          >
            {isLoading ? "Saving..." : existingContext ? "Update" : "Save"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
